import React from 'react';
import styled from 'styled-components/macro';
import Typography from '@material-ui/core/Typography';
import { FieldTypeIcon } from '../../../internal-components/form-builder/FormBuilderFieldForm';
import Select from '../../engine/SelectEngine';
import useTranslations from '../../../hooks/useTranslations';
import FieldsConditionsQueryBuilderEngine from '../engine/FieldsConditionsQueryBuilderEngine';
import { AndOr } from './ConditionsQueryBuilderView';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Animation from '../../Animation';
import { TextString } from '../../Text';
import { conditionDisabled } from '../../../internal-components/conditional-logic/compareTypeMap';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  ${({ $wideConditionValueContainer }) =>
    $wideConditionValueContainer &&
    `div[class^='MuiCollapse-container'] {flex: 1;}
        span[class*='AlignmentSpan'] {flex: 1;}
    `}
`;

const PrefixText = styled.span`
  margin-right: 0.8rem;
  margin-left: ${({ $hierarchyLvl = 0 }) => $hierarchyLvl * 0.8}rem;
`;

const SectionDiv = styled.div`
  position: relative;
`;

export const ConditionRow = styled.div`
  display: flex;
  align-items: center;
  opacity: ${({ $marked }) => ($marked ? '0.4' : '1')};
  transition: 0.3s;

  & > span {
    margin-right: 0.8rem;
  }
`;

const AddRemoveBtnsDiv = styled.div`
  display: flex;
  margin-left: auto;
`;

const wideConditionValueContainerVariantList = {
  'form-builder': true
};

const FieldsQueryBuilderView = props => {
  const t = useTranslations();

  return (
    <Container
      $wideConditionValueContainer={
        wideConditionValueContainerVariantList[props.variant]
      }
    >
      {props.useStaticFieldId ? (
        ''
      ) : (
        <SectionDiv>
          <PrefixText>{props.sentencePrefix}</PrefixText>
          <Tooltip
            arrow
            title={props.fieldName.length > 25 ? props.fieldName : ''}
          >
            <Select
              variant={props.variant}
              // fullWidth
              // customCSS="max-width: 42rem;"
              searchable
              // virtualize
              // virtualiziztionProps={{ itemSize: 60, width: '100%' }}
              data-testid={`logicbuilder-condition-select-field`}
              value={props.fieldValue}
              onChange={props.updateField}
              placeholder="Choose a question"
              options={props.inputFieldChoices.map(
                ({ internalId: id, uiType, title, lookupInfo }) => ({
                  value: id,
                  'data-testid': `logicbuilder-condition-select-field-value-${id}`,
                  text: title || `Untitled ${uiType} field`,
                  uiType,
                  disabled: conditionDisabled.includes(uiType),
                  isSubField: lookupInfo && lookupInfo.subFieldInfo
                })
              )}
              openMenuOptionParser={opt => {
                return (
                  <>
                    <FieldTypeIcon fieldType={opt.uiType} />
                    <span
                      style={{
                        maxWidth: 400,
                        display: 'inline-block',
                        whiteSpace: 'normal',
                        width: 'calc(100% - 64px)'
                      }}
                    >
                      <Typography>
                        <TextString content={opt.text} />
                      </Typography>
                    </span>
                  </>
                );
              }}
            />
          </Tooltip>
        </SectionDiv>
      )}

      {Object.entries(props.conditions).map(([id, cond], i) => (
        <Animation
          key={id}
          type="collapse"
          in={props.inDeleteSequence !== id}
          onExited={() => props.removeCondition(id)}
          role="animation-box"
        >
          <ConditionRow $marked={props.hoverOn === id}>
            {i === 0 ? (
              <span
              // $hierarchyLvl={1}
              >
                {t(`logic.${props.comparisonType}.is`)}
              </span>
            ) : (
              <AndOr value={props.internOp} update={props.updateInternOp} />
            )}
            <FieldsConditionsQueryBuilderEngine
              conditions={cond}
              compareType={props.comparisonType}
              update={conds =>
                props.updateConditions(id, conds, props.comparisonType)
              }
            />
            <AddRemoveBtns
              addCondition={props.addCondition}
              setHoverOn={props.setHoverOn}
              onRemove={props.setInDeleteSequence}
              id={id}
              tooltipText={'comparison'}
            />
          </ConditionRow>
        </Animation>
      ))}
    </Container>
  );
};

export const AddRemoveBtns = ({
  addCondition = () => {},
  setHoverOn = () => {},
  onRemove = () => {},
  id,
  tooltipText = 'condition'
}) => {
  return (
    <AddRemoveBtnsDiv>
      <Tooltip arrow title={`Add ${tooltipText}`}>
        <IconButton size="small" onClick={addCondition}>
          <AddIcon />
        </IconButton>
      </Tooltip>
      <Tooltip arrow title={`Remove ${tooltipText}`}>
        <IconButton
          size="small"
          onMouseEnter={() => setHoverOn(id)}
          onMouseLeave={() => setHoverOn(null)}
          onClick={() => onRemove(id)}
        >
          <RemoveIcon />
        </IconButton>
      </Tooltip>
    </AddRemoveBtnsDiv>
  );
};

export default FieldsQueryBuilderView;
